import { useEffect, useRef, useState, type ReactNode } from 'react'
import type { Me } from '../api/types'
import { IS_DEMO } from '../api/demo'
import { LoginAsBanner } from './LoginAs'
import { BrandMark, Wordmark } from './Brand'

/**
 * 'reports' — the catalogue page — was removed 6 Aug 2026. It listed the one
 * built report plus two "planned" placeholders, so for every real user it was a
 * click between the sidebar and the report the sidebar already linked to.
 *
 * 'news' replaced it as the landing page and inherits the one property worth
 * keeping: it is reachable by EVERYONE. Without such a page, somebody granted no
 * report and holding no platform role signs in to an empty frame.
 */
export type PageId =
  | 'news' | 'recv-aging' | 'sales' | 'material-planning' | 'inventory-aging' | 'statement'
  | 'maintenance' | 'permissions'

/**
 * THE SHELL.
 *
 * A navy rail framing a white data canvas. The rail carries identity and
 * navigation and never data; the canvas carries data and never chrome. That
 * separation is the whole layout, and it is why a twelve-column matrix stays
 * legible — nothing competes with it for attention inside the white area.
 *
 * Navy is the brand and is used for exactly three things: this rail, anything
 * interactive, and the selected state. Never for emphasis, never for decoration.
 * Privilege is violet instead, deliberately outside the brand hue, so that
 * "you are using standing all-data access" can never be mistaken for an ordinary
 * highlight.
 */

interface NavItem {
  id: PageId
  label: string
  group: 'News' | 'Reports' | 'Administration'
  /**
   * Roles that may see this item. Hiding is a courtesy, not a control — the API
   * refuses regardless, and it is the API's refusal that is the security
   * boundary. Anyone can edit what a browser renders.
   */
  roles?: string[]
  /**
   * The report this entry opens. Present ⇒ the entry is shown only when the
   * identity may actually open that report.
   *
   * Same disclaimer as `roles`: the report procedure refuses regardless, and its
   * refusal is the boundary. This stops the menu ADVERTISING a door the platform
   * will not open — which is a correctness problem for the reader, not a
   * security one. Somebody shown "Receivables", who clicks it and gets a refusal
   * panel, learns that the platform is broken; it is in fact working exactly as
   * configured, and nobody told them.
   */
  reportSlug?: string
  icon: ReactNode
}

/* Icons are inline paths on a 16px grid. No icon library: this runs on a
   locked-down desktop that may have no outbound network, and navigation that
   fails to load is navigation that does not exist. */
const Icon = {
  news: (
    <path
      d="M2.5 4.5h8v8h-8z M12.5 6.5h1v5a1 1 0 0 1-1 1 M4.5 7h4 M4.5 9.5h4"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  aging: <path d="M2.5 12.5V9 M6 12.5V5.5 M9.5 12.5V3 M13 12.5v-5" strokeWidth="1.6" strokeLinecap="round" />,
  // A rising line with its last reading marked: sales as movement, where the
  // aging icon is bars-as-balance. Same stroke weight, same 16px grid.
  // Stacked crates: this report is about what is in the warehouse and what is
  // on its way there, not about money.
  planning: <path d="M2 9.5h11M2 12.5h11M4.5 9.5v3M10.5 9.5v3M4 6.5h7l-.6-3H4.6z" strokeWidth="1.4" strokeLinejoin="round" />,
  // A box with a clock hand on its face: stock, measured in time — how long it
  // has sat, and how long it has left. Same stroke weight, same 16px grid.
  inventoryAging: (
    <>
      <path d="M2.5 5.5l5.5-3 5.5 3v6l-5.5 3-5.5-3z" fill="none" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M8 6.2v2.6l1.8 1.1" fill="none" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  // A page with a folded corner and ruled lines: a document that leaves the
  // company, where the other reports are screens. Same stroke, same grid.
  statement: (
    <>
      <path d="M4 2.5h5.5L12 5v8.5H4z M9.5 2.5V5H12" fill="none" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M6 8h4 M6 10h4 M6 12h2.5" fill="none" strokeWidth="1.3" strokeLinecap="round" />
    </>
  ),
  sales: (
    <>
      <path d="M2 12.5l3.5-4 2.5 2L13.5 4" fill="none" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10 3.5h3.5V7" fill="none" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  maintenance: (
    <path
      d="M8 2v2.2 M8 11.8V14 M2 8h2.2 M11.8 8H14 M4 4l1.5 1.5 M10.5 10.5L12 12 M12 4l-1.5 1.5 M5.5 10.5L4 12"
      strokeWidth="1.3"
      strokeLinecap="round"
    />
  ),
  permissions: (
    <path
      d="M8 2.2l4.5 2v3.4c0 2.6-1.9 4.7-4.5 5.6-2.6-.9-4.5-3-4.5-5.6V4.2z"
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
  ),
}

const NAV: NavItem[] = [
  // Its own section, above the reports. It is not a report — it is the front
  // page — and filing it under "Reports" put it at the top of a list it does
  // not belong to.
  //
  // No `roles` and no `reportSlug`: open to everybody. It is the only entry
  // with no gate, deliberately — see PageId.
  { id: 'news', label: 'News', group: 'News', icon: Icon.news },
  { id: 'recv-aging', label: 'Receivables', group: 'Reports', reportSlug: 'receivables', icon: Icon.aging },
  { id: 'sales', label: 'Sales', group: 'Reports', reportSlug: 'sales', icon: Icon.sales },
  { id: 'material-planning', label: 'Material Planning', group: 'Reports', reportSlug: 'material-planning', icon: Icon.planning },
  { id: 'inventory-aging', label: 'Inventory Aging', group: 'Reports', reportSlug: 'inventory-aging', icon: Icon.inventoryAging },
  { id: 'statement', label: 'Statement of Account', group: 'Reports', reportSlug: 'statement-of-account', icon: Icon.statement },
  { id: 'maintenance', label: 'Maintenance', group: 'Administration', roles: ['SystemAdmin'], icon: Icon.maintenance },
  // System Admins, since the role that existed for this job was removed (033),
  // and System Development, whose page shows the Developer mode tab alone (D5).
  // Kept in step with mayUseDeveloperMode and App.tsx's canReach.
  {
    id: 'permissions',
    // "Access", not "User permissions". The screen stopped being a read-only
    // view of D365's permission table when it gained the two things this
    // platform actually grants, and a nav label that promises one job while the
    // page does three is where "so where do I give somebody permission?" starts.
    label: 'Access',
    group: 'Administration',
    roles: ['SystemAdmin', 'SystemDevelopment'],
    icon: Icon.permissions,
  },
]

/**
 * THE DATA REPORTS, derived from NAV rather than listed again.
 *
 * Two other screens need to know which reports exist and which page each one
 * opens: Maintenance, whose scope picker takes a report offline, and the News
 * composer, which may only offer a report a card can actually point at.
 *
 * Both used to keep their own copy of this list. Both were still naming
 * Receivables and Sales on 8 Sep 2026, a day after Material Planning shipped
 * with its own page, its own procedures and its own sidebar entry — so it
 * could not be taken offline and no update about it could be published. The
 * copies were not wrong when written; they were simply never the thing anyone
 * remembers to change.
 *
 * NAV is the one list that cannot go stale, because a report missing from it
 * has no sidebar entry and is therefore unreachable — a mistake nobody ships
 * twice. Deriving from it means adding a report to NAV is the whole job.
 *
 * `group === 'Reports'` is the filter, not `reportSlug`, because Access is a
 * governable object with a slug too ('permissions') and is deliberately an
 * administration surface rather than a report — the distinction both callers
 * were already making by hand, now made once.
 */
export const REPORT_PAGES: { slug: string; name: string; page: PageId }[] = NAV
  .filter((n) => n.group === 'Reports' && n.reportSlug)
  .map((n) => ({ slug: n.reportSlug!, name: n.label, page: n.id }))

export function Shell({
  me,
  surface,
  page,
  onNavigate,
  onSignOut,
  viewAs,
  onOpenViewAs,
  onExitViewAs,
  closedReports = [],
  notice = null,
  children,
}: {
  /** WHO IS SIGNED IN. Drives the footer and the privilege chip. Never borrowed. */
  me: Me | null
  /**
   * WHAT THE PLATFORM OFFERS: the same object normally, the TARGET's roles and
   * report list while View As is on. Drives the menu and nothing else.
   *
   * Two props rather than one because the footer and the menu answer different
   * questions — "who am I" and "what would they see" — and one object cannot be
   * the honest answer to both at once.
   */
  surface: Me | null
  /**
   * Null when this identity can reach no page at all — possible since the
   * catalogue page was removed, because it was the one screen everybody could
   * open. Nothing in the rail is then highlighted, which is correct.
   */
  page: PageId | null
  onNavigate: (p: PageId) => void
  onSignOut: () => void
  /** Whose permissions the session is currently rendering through, or null. */
  viewAs: string | null
  onOpenViewAs: () => void
  onExitViewAs: () => void
  /**
   * Slugs of reports that are closed for maintenance right now, so the rail can
   * say so before the entry is clicked.
   *
   * The entry stays CLICKABLE and stays in place. Removing it would be the
   * `reportSlug` treatment, and that is for a report the identity may never
   * open — a different fact, and one that does not change back in two hours.
   * Somebody who cannot find Receivables where it has always been goes looking
   * for a fault; somebody who clicks it and is told it is closed has an answer.
   *
   * A report under read grace is deliberately absent from this list: it works.
   */
  closedReports?: string[]
  /**
   * A standing strip above the page, under the View As banner. Used for a
   * maintenance window that is still serving reads — the one case where the
   * screen is not replaced, so the notice has to live somewhere.
   */
  notice?: ReactNode
  children: ReactNode
}) {
  /* THE MENU IS BUILT FROM `surface`, THE FOOTER FROM `me`.
     -----------------------------------------------------------------------
     Under View As, `surface` carries the TARGET's roles and reports while `me`
     stays the real caller. So the sidebar shows what that person would be
     offered — no Maintenance, no Access, and no report they cannot open — while
     the footer and the banner keep saying who is actually signed in.

     One object for both was the first attempt, and it printed the real name
     beside the borrowed status: "demo.user@redachem.com — No platform role".

     Before this, View As changed the report and nothing else: an administrator
     checking whether a new joiner could see Receivables saw it in the sidebar
     and concluded yes. The feature was answering a different question from the
     one it appeared to answer, confidently. */
  const held = surface?.roles ?? []
  const openable = surface?.reports ?? []
  const visible = (n: NavItem) =>
    (!n.roles || n.roles.some((r) => held.includes(r))) &&
    (!n.reportSlug || openable.includes(n.reportSlug))

  // Rail order, top to bottom. News first: it is the landing page, so the rail
  // reads in the same order the platform opens in.
  const groups: NavItem[][] = (['News', 'Reports', 'Administration'] as const).map((g) =>
    NAV.filter((n) => n.group === g).filter(visible),
  )

  /* THE PHONE MENU — added 29 Sep 2026, when phones came into scope.
     -----------------------------------------------------------------------
     On a phone the rail leaves the row and becomes a drawer over the page,
     opened from a navy bar at the top. It is the SAME <nav>, not a second copy
     of it: one list of entries, one set of visibility rules, one place for the
     next report to be added. Only its position changes, and every class that
     moves it carries `phone:` — defined in styles.css as a window under 768px
     wide, or a touch screen under 480px tall — so nothing here renders
     differently on a laptop. `menuOpen` can only become true by pressing a
     button that exists only on a phone.

     Choosing an entry closes the drawer, because on a phone the drawer covers
     the page the reader just asked for. */
  const [menuOpen, setMenuOpen] = useState(false)
  const menuButton = useRef<HTMLButtonElement>(null)
  const navRef = useRef<HTMLElement>(null)
  const go = (p: PageId) => {
    setMenuOpen(false)
    onNavigate(p)
  }
  useEffect(() => {
    if (!menuOpen) return
    // Focus moves into the drawer so a keyboard or a screen reader lands in
    // the menu it just opened, and Escape hands it back to the button.
    navRef.current?.querySelector<HTMLElement>('.rail-item')?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setMenuOpen(false)
      menuButton.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    // A window that stops being a phone while the drawer is open — widened on
    // a desktop — would keep the scrim over a page whose menu button has gone.
    // The query is the `phone:` variant's, from styles.css.
    const phone = window.matchMedia('(max-width: 767.98px), (max-height: 479.98px) and (pointer: coarse)')
    const onChange = () => {
      if (!phone.matches) setMenuOpen(false)
    }
    phone.addEventListener('change', onChange)
    return () => {
      window.removeEventListener('keydown', onKey)
      phone.removeEventListener('change', onChange)
    }
  }, [menuOpen])
  const here = NAV.find((n) => n.id === page)

  return (
    /* A VIEWPORT SHELL: the window never scrolls, only the content area does.
     *
     * h-dvh + overflow-hidden pins the whole frame to the visible viewport, so
     * the rail and the banners stay put and <main> scrolls inside them. The
     * previous layout was min-h-full with the PAGE as the scroll container, so
     * scrolling a long report carried the navigation off the top of the screen
     * — on the aging matrix, which is 15 business units before the total row,
     * the rail was gone before the reader reached the bottom.
     *
     * `overflow-hidden` here also stops a wide table pushing the whole document
     * sideways; each table already scrolls within its own container.
     *
     * print:h-auto print:overflow-visible — WITHOUT these the printed
     * Salesperson Review would be clipped to a single screen's height, because
     * the print stylesheet inherits this frame. */
    <div className="flex h-dvh flex-col overflow-hidden bg-(--color-surface-2) print:h-auto print:overflow-visible">
      {IS_DEMO && <DemoBanner />}

      {/* Above the navigation and outside the page area on purpose: it must be
          visible whichever screen the administrator wanders onto, and must not
          scroll away with the report they are reading. */}
      {viewAs && (
        <LoginAsBanner target={viewAs} actor={surface?.actor?.email ?? null} onExit={onExitViewAs} />
      )}

      {/* Below the View As banner, because whose eyes you are looking through
          outranks what is happening to the service. Outside <main> so it does
          not scroll away with the report it is describing, and unpadded so the
          notice can run edge to edge like the two bars above it. */}
      {notice && <div className="shrink-0 print:hidden">{notice}</div>}

      {/* THE PHONE BAR. `hidden` on every screen the platform was built for,
          so it takes no space there; on a phone it is the only chrome above
          the page. It names where the reader is, because the drawer that
          would otherwise say so is closed most of the time. */}
      <div className="on-navy hidden h-13 shrink-0 items-center gap-2.5 bg-(--color-navy-700) pl-1.5 pr-4 text-white phone:flex print:hidden">
        <button
          ref={menuButton}
          type="button"
          data-phone-menu
          onClick={() => setMenuOpen(true)}
          aria-label="Open menu"
          aria-expanded={menuOpen}
          aria-controls="mbi-main-nav"
          className="grid h-11 w-11 shrink-0 place-items-center rounded text-white/90 transition-colors hover:bg-white/10 hover:text-white"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" aria-hidden>
            <path d="M3 5.5h14M3 10h14M3 14.5h14" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </button>
        <BrandMark size={26} tone="white" />
        <span className="min-w-0 flex-1 truncate text-(length:--text-base) font-semibold">
          {here ? here.label : 'MBI'}
        </span>
      </div>

      {/* min-h-0 is load-bearing: a flex child defaults to min-height:auto,
          which refuses to shrink below its content and would let the row grow
          past the viewport — putting the scrollbar back on the window and
          undoing the whole arrangement. */}
      <div className="flex min-h-0 flex-1 print:block print:min-h-0">
        {/* The drawer's scrim. It exists only while the drawer is open, and
            the drawer can only be opened from the phone bar. */}
        {menuOpen && (
          <div
            aria-hidden
            onClick={() => setMenuOpen(false)}
            className="anim-scrim-in fixed inset-0 z-40 bg-(--color-ink)/40"
          />
        )}
        <nav
          ref={navRef}
          id="mbi-main-nav"
          /* min-h-0 so the item list's own overflow-y-auto has room to work:
             without it the nav grows to fit its items and the rail itself
             becomes taller than the viewport. The signed-in block at the
             bottom must stay reachable however many reports are added.

             Every `phone:` class turns the rail into the phone drawer: out
             of the row, over the page, full height, off-screen until opened.
             `invisible` while closed, not merely translated, so a keyboard
             cannot tab into a menu nobody can see. Visibility is animated only
             on the way OUT, so the drawer can slide away before it hides; on
             the way in it is visible at once, because a transition from
             hidden starts at hidden and the first entry could not take focus
             in the frame the drawer opened. */
          className={`on-navy flex min-h-0 w-(--shell-nav) shrink-0 flex-col bg-(--color-navy-700) text-white print:hidden phone:fixed phone:inset-y-0 phone:left-0 phone:z-50 phone:w-[min(18rem,85vw)] phone:shadow-(--shadow-raised) phone:duration-200 ${
            menuOpen
              ? 'phone:transition-transform'
              : 'phone:invisible phone:-translate-x-full phone:transition-[transform,visibility]'
          }`}
          aria-label="Main"
        >
          <div className="rail-brand flex items-center gap-2.5 px-4 py-4">
            <BrandMark size={30} tone="white" />
            <span className="rail-hide-collapsed">
              <Wordmark tone="white" />
            </span>
            {/* The drawer's own way out, for a reader who does not know a tap
                outside it closes it too. Phone only, like the drawer. */}
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false)
                menuButton.current?.focus()
              }}
              aria-label="Close menu"
              className="-mr-2 ml-auto hidden h-10 w-10 shrink-0 place-items-center rounded text-white/80 transition-colors hover:bg-white/10 hover:text-white phone:grid"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" aria-hidden>
                <path d="M4.5 4.5l9 9M13.5 4.5l-9 9" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          <div className="mx-4 h-px bg-white/12" />

          <div className="rail-list flex-1 overflow-y-auto px-3 py-4">
            {groups.map((items, gi) =>
              items.length === 0 ? null : (
                <div key={gi} className="mb-5">
                  {/* THE HEADING IS A RULE WITH A LABEL SET INTO IT.
                      ------------------------------------------------------
                      A bare uppercase caption divides a list only by being
                      slightly further from the item above it than the items
                      are from each other — a few pixels of whitespace doing
                      the work of a boundary. Once there were three sections
                      the rail read as one long column with faint words in it.

                      The line is the boundary; the label sits in the break so
                      the section is named and separated by the same object.
                      Same white/12 hairline as the divider under the wordmark
                      and the one above the identity card, so the rail has one
                      rule weight rather than three.

                      The stub on the left starts the rule at the rail's edge
                      instead of at the label, so the break is unmistakably a
                      break and not an underline that lost its start. */}
                  <div
                    className="rail-group flex items-center gap-2 pb-2 text-(length:--text-micro) font-semibold uppercase tracking-[0.07em] text-white/45"
                  >
                    <span className="h-px w-2 shrink-0 rounded-full bg-white/12" />
                    <span className="rail-hide-collapsed">{items[0].group}</span>
                    <span className="h-px flex-1 rounded-full bg-white/12" />
                  </div>
                  {items.map((item) => {
                    const active = page === item.id
                    const shut = !!item.reportSlug && closedReports.includes(item.reportSlug)
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => go(item.id)}
                        aria-current={active ? 'page' : undefined}
                        /* The tooltip is the label for a mouse once the rail
                           is collapsed. The label itself is clipped rather
                           than removed, so the accessible name survives.
                           The state is appended rather than replacing it,
                           because at 80px wide this tooltip is the only thing
                           naming the entry at all. */
                        title={shut ? `${item.label} — closed for maintenance` : item.label}
                        className={`rail-item relative mb-0.5 flex w-full items-center gap-2.5 rounded px-2.5 py-2 text-left text-(length:--text-base) transition-colors duration-100 ${
                          active
                            ? 'bg-white/14 font-semibold text-white'
                            : 'font-medium text-white/72 hover:bg-white/8 hover:text-white'
                        }`}
                      >
                        {/* The selected marker is a rule, not a fill. A filled
                            pill at this size swallows the label. */}
                        <span
                          aria-hidden
                          className={`absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-white transition-opacity ${
                            active ? 'opacity-100' : 'opacity-0'
                          }`}
                        />
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          stroke="currentColor"
                          aria-hidden
                          className="shrink-0 opacity-90"
                        >
                          {item.icon}
                        </svg>
                        <span className="rail-hide-collapsed">{item.label}</span>

                        {/* THE CLOSED MARK.
                            A dot, not a strikethrough and not a dimmed label:
                            the entry is still a place you can go, and what it
                            will tell you when you get there is the closed
                            screen. Amber is what maintenance means everywhere
                            in this product, lifted to the on-navy value
                            because #b45309 is invisible on this rail.

                            ml-auto so it sits at the far edge rather than
                            against the label, which would read as part of the
                            word. It is clipped with the label when the rail
                            collapses to 80px; the title attribute above is
                            what carries the state at that width. */}
                        {shut && (
                          <span
                            aria-hidden
                            className="rail-hide-collapsed ml-auto inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-(--color-warn-on-navy)"
                          />
                        )}
                        {shut && <span className="sr-only">Closed for maintenance</span>}
                      </button>
                    )
                  })}
                </div>
              ),
            )}
          </div>

          {me && (
            <SignedInAs
              me={me}
              onSignOut={onSignOut}
              viewAs={viewAs}
              onOpenViewAs={() => {
                // The picker opens over the page; the drawer must not sit on top of it.
                setMenuOpen(false)
                onOpenViewAs()
              }}
              onExitViewAs={() => {
                setMenuOpen(false)
                onExitViewAs()
              }}
            />
          )}
        </nav>

        {/* TWO LAYERS, AND THE INNER ONE IS WHY.
         *
         * <main> is a fixed-height box that never scrolls. Inside it sits a
         * scroller that is exactly its height. A page therefore gets to choose:
         *
         *   * do nothing, and it scrolls as a document — the Reports,
         *     Maintenance and Permissions screens, which are lists of prose
         *     and controls and should behave like a normal page;
         *
         *   * set `h-full` and manage its own internal scrolling — the report
         *     screens, where the title, tabs and KPI strip must stay put and
         *     only the grid moves.
         *
         * The second case works without a flag anywhere: a page that is exactly
         * h-full never exceeds the scroller, so the outer scrollbar simply
         * never appears. Nothing has to be told which kind of page it is, and a
         * page that forgets still behaves correctly rather than being clipped —
         * which is the failure mode worth designing out.
         *
         * min-w-0 lets a wide grid scroll inside its own container instead of
         * stretching this one. min-h-0 is the flexbox default fighting back
         * again: without it this refuses to shrink below its content. */}
        {/* @container MAKES THE BREAKPOINTS ASK THE RIGHT QUESTION.
            Every layout variant in this app used to measure the WINDOW while
            living in a box 304px narrower, so Tailwind's xl: fired at a 1280px
            window inside a 976px box — and the Sales leaderboards went from 2
            columns of 453px to 4 of 219px as the window got ONE PIXEL wider.
            Pages now use @-variants (@3xl:, @5xl:, @6xl:) which measure THIS
            element, so a column count can never contradict the space it has. */}
        <main className="@container min-h-0 min-w-0 flex-1 overflow-hidden print:overflow-visible">
          <div className="content-gutter scroll-slim h-full overflow-y-auto px-7 py-6 print:h-auto print:overflow-visible">
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}

function SignedInAs({
  me,
  onSignOut,
  viewAs,
  onOpenViewAs,
  onExitViewAs,
}: {
  me: Me
  onSignOut: () => void
  viewAs: string | null
  onOpenViewAs: () => void
  onExitViewAs: () => void
}) {
  const initials = (me.user || '?')
    .split(/[.@]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('')

  /* THE ADDRESS, SPLIT AT THE @.
     ------------------------------------------------------------------------
     It used to be one truncated line: "mohamed.m.dsa7@redachem…". The part
     that got cut is the DOMAIN, which is the same for everybody here, but the
     ellipsis made the whole thing read as unreadable — and on a platform where
     "whose eyes am I looking through" is a real question, your own identity is
     the last thing that should be clipped.

     Split, the local part gets the full width and reads in full; the domain
     sits under it, quieter, and truncates harmlessly if it ever needs to. */
  const [local, domain] = me.user.includes('@')
    ? [me.user.slice(0, me.user.lastIndexOf('@')), me.user.slice(me.user.lastIndexOf('@') + 1)]
    : [me.user, '']

  return (
    /* A CARD, not a bare strip under a rule.
       ------------------------------------------------------------------------
       Everything else in the rail is navigation — one flat list of places to
       go. This block is not a place, it is a statement about WHO YOU ARE and
       what you may do, and it was rendered with the same visual weight as a
       menu item. Lifting it onto its own surface separates the two without
       adding a colour: a slightly lighter navy is enough on this background. */
    /* THE RULE ABOVE IT SEPARATES TWO KINDS OF THING.
       Above: navigation — where you can go. Below: who you are. The card alone
       grouped the identity block but did not divide it from the list, so the
       two still read as one continuous rail. */
    <div className="rail-identity border-t border-white/12 p-3">
      <div className="rail-identity-card rounded-lg border border-white/12 bg-white/8 p-3">
        <div className="rail-avatar-row flex items-start gap-2.5" title={me.user}>
          <span
            aria-hidden
            className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-white/15 text-(length:--text-small) font-semibold text-white"
          >
            {initials}
          </span>
          <div className="rail-hide-collapsed min-w-0 flex-1 leading-tight">
            <div className="truncate text-(length:--text-small) font-semibold text-white" title={me.user}>
              {local}
            </div>
            {domain && (
              <div className="truncate text-(length:--text-micro) text-white/45">@{domain}</div>
            )}
          </div>
        </div>

        {/* ROLE AND PRIVILEGE ON ONE ROW, as two chips of the same shape.
            They answer one question together — what am I allowed to do — and
            the old layout had the role as grey text under the name and the
            privilege as a chip further down, so they read as unrelated. */}
        <div className="rail-hide-collapsed mt-2.5 flex flex-wrap gap-1.5">
          <span className="inline-flex items-center rounded-full bg-white/10 px-2 py-0.5 text-(length:--text-micro) font-medium text-white/85">
            {/* Under the new model a reader's standing is their HR role (05 7's
                Shell row); /me sends none under legacy, which keeps today's words. */}
            {me.roles.length
              ? me.roles.map((r) => roleLabel(r, me.permissionsModel === 'v2')).join(' · ')
              : me.hrRole?.label ?? 'No platform role'}
          </span>

          {/* Standing all-data access is the accepted risk in decision D-05, and
              the agreed mitigation is attribution. Showing the holder that they
              are using it is the visible half of that: nobody should have this
              power without being reminded they hold it.

              While acting as somebody else this footer is THEIRS, so the chip
              follows their bypass and not the administrator's -- claiming "full
              data access" for somebody who does not would be a lie the user could
              check against the grid beside it. */}
          {me.seesAllData ? (
            viewAs ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2 py-0.5 text-(length:--text-micro) font-semibold text-white/80">
                <Dot /> Full data access set aside
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-(--color-warn)/30 px-1.5 py-0.5 text-(length:--text-micro) font-semibold text-white">
                <Dot /> Full data access
              </span>
            )
          ) : (
            <span className="inline-flex items-center rounded-full bg-white/10 px-2 py-0.5 text-(length:--text-micro) font-medium text-white/70">
              {me.scopeNodeCount} scope{me.scopeNodeCount === 1 ? '' : 's'}
            </span>
          )}
        </div>

        {/* VIEW AS IS A BUTTON; SIGN OUT IS NOT.
            Both used to be small underlined links side by side, which gave a
            routine action and a terminal one identical weight. View As is the
            thing an administrator reaches for daily, so it gets a full-width
            control. Sign out sits below a rule, quiet, where it cannot be hit
            by aiming at the other.

            While borrowing an identity the control INVERTS to the privileged
            violet — the same colour as the banner overhead, so the way out is
            the same colour as the reminder that you are in. */}
        {me.canViewAsData && (
          <button
            type="button"
            onClick={viewAs ? onExitViewAs : onOpenViewAs}
            className={`rail-identity-btn mt-3 w-full rounded border px-2.5 py-1.5 text-(length:--text-micro) font-semibold transition-colors ${
              viewAs
                ? 'border-transparent bg-(--color-privileged) text-white hover:bg-(--color-privileged)/85'
                : 'border-white/15 bg-white/5 text-white/85 hover:bg-white/12 hover:text-white'
            }`}
          >
            <span className="rail-hide-collapsed">{viewAs ? 'Stop sign in as' : 'Sign in as someone else'}</span>
            <span className="rail-only-collapsed" aria-hidden>{viewAs ? 'Stop' : 'Sign in as'}</span>
          </button>
        )}

        <div
          className={`text-center ${
            me.canViewAsData ? 'mt-2.5 border-t border-white/10 pt-2' : 'mt-3'
          }`}
        >
          <button
            type="button"
            onClick={onSignOut}
            className="text-(length:--text-micro) font-medium text-white/50 underline-offset-2 transition-colors hover:text-white hover:underline"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * 'SystemAdmin' reads as a database value; 'System Admin' reads as a job. Under
 * the new model MasterViewer is "System Master Reader" (D30), as Platform roles
 * calls it; today's model keeps "Master Viewer" until the hour.
 */
function roleLabel(code: string, isV2: boolean): string {
  return (
    {
      SystemAdmin: 'System Admin',
      MasterViewer: isV2 ? 'System Master Reader' : 'Master Viewer',
      SystemDevelopment: 'System Development',
    }[code] ?? code
  )
}

function Dot() {
  return <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current" />
}

function DemoBanner() {
  return (
    <div className="flex items-center justify-center gap-2 bg-(--color-warn) px-4 py-1.5 text-(length:--text-small) font-semibold text-white">
      DEMO MODE — invented data, no authentication. Nothing here is a real balance.
    </div>
  )
}
